// src/pages/StudentDetails.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarIcon,
  MapPinIcon,
  IdentificationIcon,
  HeartIcon,
  PencilSquareIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CameraIcon,
  TrashIcon,
  PrinterIcon,
  UserGroupIcon,
  CakeIcon,
  GlobeAltIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../api/client";
import toast from "react-hot-toast";
import { FireLoader } from "../components/Loader";
import { useAuth } from "../context/AuthContext";
import { showWarning } from "../utils/alert";

// Helper function to get the full image URL
const getImageUrl = (profilePicture) => {
  if (!profilePicture) return null;
  if (
    profilePicture.startsWith("http://") ||
    profilePicture.startsWith("https://")
  ) {
    return profilePicture;
  }
  const apiBaseUrl =
    process.env.REACT_APP_API_URL ||
    "https://lavender-falcon-985378.hostingersite.com/api/v1";
  const baseUrl = apiBaseUrl.replace("/api/v1", "");
  if (profilePicture.startsWith("/uploads")) {
    return `${baseUrl}${profilePicture}`;
  }
  if (profilePicture.startsWith("uploads/")) {
    return `${baseUrl}/${profilePicture}`;
  }
  return `${baseUrl}/uploads/students/${profilePicture}`;
};

// Helper function to get full name with middle name
const getFullName = (firstName, middleName, lastName) => {
  const parts = [];
  if (firstName) parts.push(firstName);
  if (middleName && middleName.trim()) parts.push(middleName.trim());
  if (lastName) parts.push(lastName);
  return parts.join(" ") || "N/A";
};

// Escape values inserted into the printable HTML document.
const escapeHtml = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

export default function StudentDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || "").trim().toLowerCase() === "starter";
  const userRole = String(user?.role || "").toLowerCase();
  const canManageStudent = ["admin", "school_admin", "super_admin"].includes(
    userRole,
  );
  const canEditGrades = canManageStudent || userRole === "teacher";
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [schoolName, setSchoolName] = useState("School");
  const [isMobile, setIsMobile] = useState(false);

  // Detect if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };
    checkMobile();
  }, []);

  useEffect(() => {
    fetchStudentDetails();
  }, [id]);

  const fetchStudentDetails = async () => {
    try {
      setLoading(true);
      const [response, currentUserResponse, schoolProfileResponse] =
        await Promise.all([
          apiClient.get(`/students/${id}`),
          apiClient.get("/users/me").catch(() => null),
          apiClient.get("/schools/profile").catch(() => null),
        ]);
      const studentData = response.data?.data || response.data;
      const currentUserData =
        currentUserResponse?.data?.data || currentUserResponse?.data || {};
      const schoolProfileData =
        schoolProfileResponse?.data?.data ||
        schoolProfileResponse?.data?.school ||
        schoolProfileResponse?.data ||
        {};

      const resolvedSchoolName =
        schoolProfileData.schoolName ||
        schoolProfileData.name ||
        studentData.school?.name ||
        studentData.school?.schoolName ||
        studentData.school_name ||
        studentData.schoolName ||
        currentUserData.school?.name ||
        currentUserData.school?.schoolName ||
        currentUserData.school_name ||
        currentUserData.schoolName ||
        currentUserData.user?.school?.name ||
        currentUserData.user?.school_name;

      if (resolvedSchoolName) {
        setSchoolName(resolvedSchoolName);
      }

      const parentUser =
        studentData.parentUser ||
        studentData.parent_user ||
        studentData.parent ||
        {};

      const mappedStudent = {
        id: studentData.id,
        firstName: studentData.first_name || studentData.firstName || "",
        lastName: studentData.last_name || studentData.lastName || "",
        middleName: studentData.middle_name || studentData.middleName || "",
        dateOfBirth: studentData.date_of_birth || studentData.dateOfBirth,
        gender: studentData.gender,
        nationality: studentData.nationality,
        religion: studentData.religion,
        tribe: studentData.tribe,
        address: studentData.address,
        city: studentData.city,
        state: studentData.state,
        country: studentData.country,
        phone: studentData.phone,
        email: studentData.email,
        admissionNumber:
          studentData.admission_number || studentData.admissionNumber,
        classId: studentData.current_class_id || studentData.classId,
        academicYearId:
          studentData.academic_year_id || studentData.academicYearId,
        enrollmentDate:
          studentData.enrollment_date || studentData.enrollmentDate,
        graduationDate:
          studentData.graduation_date || studentData.graduationDate,
        status: studentData.status,
        isActive:
          studentData.is_active !== undefined
            ? studentData.is_active
            : studentData.status === "active",
        medicalConditions:
          studentData.medical_conditions || studentData.medicalConditions,
        allergies: studentData.allergies,
        parentFirstName:
          parentUser.first_name ||
          parentUser.firstName ||
          studentData.parent_first_name ||
          "",
        parentLastName:
          parentUser.last_name ||
          parentUser.lastName ||
          studentData.parent_last_name ||
          "",
        parentEmail: parentUser.email || studentData.parent_email || "",
        parentPhone: parentUser.phone || studentData.parent_phone || "",
        parentAddress: parentUser.address || studentData.parent_address || "",
        parentOccupation:
          parentUser.occupation || studentData.parent_occupation || "",
        parentRelationship:
          studentData.parent_relationship ||
          studentData.parentRelationship ||
          studentData.emergency_contact_relationship ||
          "",
        profilePicture:
          studentData.profile_picture ||
          studentData.profilePicture ||
          studentData.user?.profile_picture,
        user: studentData.user,
        class: studentData.class,
        createdAt: studentData.created_at || studentData.createdAt,
      };

      setStudent(mappedStudent);
      const url = getImageUrl(mappedStudent.profilePicture);
      setImageUrl(url);
      setImageError(false);
    } catch (error) {
      console.error("Error fetching student:", error);
      toast.error("Failed to load student details");
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    if (!canManageStudent) {
      toast.error("Only school administrators can change student photos");
      e.target.value = "";
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a valid image file (JPG, PNG, GIF, WEBP)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("profilePicture", file);

      const response = await apiClient.post(
        `/students/${id}/upload-profile-picture`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.data?.success !== false) {
        toast.success("Profile picture updated successfully!");
        setImageError(false);
        await fetchStudentDetails();
      } else {
        toast.error(
          response.data?.message || "Failed to upload profile picture",
        );
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(
        error.response?.data?.message || "Failed to upload profile picture",
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    if (!canManageStudent) {
      toast.error("Only school administrators can remove student photos");
      return;
    }

    if (!window.confirm("Are you sure you want to remove the profile picture?"))
      return;

    try {
      const response = await apiClient.delete(
        `/students/${id}/profile-picture`,
      );
      if (response.data?.success !== false) {
        toast.success("Profile picture removed successfully");
        await fetchStudentDetails();
      }
    } catch (error) {
      console.error("Error deleting profile picture:", error);
      toast.error("Failed to remove profile picture");
    }
  };

  const generatePrintHTML = () => {
    const fullName = getFullName(
      student.firstName,
      student.middleName,
      student.lastName,
    );
    const safeSchoolName = escapeHtml(schoolName || "School");

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${safeSchoolName} - Student Details - ${escapeHtml(fullName)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              padding: 15mm 20mm;
              color: #1a1a1a; 
              background: white;
              font-size: 12px;
            }
            @page {
              size: A4;
              margin: 0;
            }
            .print-header { 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-start;
              border-bottom: 3px solid #1a3a5c; 
              padding-bottom: 15px; 
              margin-bottom: 20px;
            }
            .print-header-left h1 { 
              font-size: 20px; 
              color: #1a3a5c; 
              font-weight: bold;
              letter-spacing: 1px;
            }
            .print-header-left p { 
              color: #555; 
              font-size: 11px;
              margin-top: 3px;
            }
            .print-header-right {
              text-align: right;
              font-size: 10px;
              color: #666;
            }
            .print-profile { 
              display: flex; 
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 25px;
              padding: 15px;
              background: #f8f9fa;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
            }
            .print-profile-info h2 { 
              font-size: 18px; 
              color: #1a3a5c;
              margin-bottom: 5px;
            }
            .print-profile-info .sub-info { 
              color: #555; 
              font-size: 12px;
            }
            .print-profile-info .sub-info span {
              display: inline-block;
              margin-right: 15px;
            }
            .print-profile-image {
              width: 100px;
              height: 120px;
              border: 2px solid #1a3a5c;
              border-radius: 4px;
              overflow: hidden;
              flex-shrink: 0;
              background: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .print-profile-image img { 
              width: 100%; 
              height: 100%; 
              object-fit: cover;
            }
            .print-profile-image .initials { 
              font-size: 32px; 
              font-weight: bold; 
              color: #1a3a5c;
            }
            .status-badge { 
              display: inline-block; 
              padding: 2px 10px; 
              border-radius: 3px; 
              font-size: 10px; 
              font-weight: 600; 
              text-transform: uppercase;
            }
            .status-active { background: #d4edda; color: #155724; }
            .status-inactive { background: #fff3cd; color: #856404; }
            .status-graduated { background: #cce5ff; color: #004085; }
            .status-transferred { background: #f8d7da; color: #721c24; }
            
            .print-section { 
              margin-bottom: 20px;
            }
            .print-section h3 { 
              font-size: 13px; 
              color: #1a3a5c; 
              border-bottom: 2px solid #e0e0e0; 
              padding-bottom: 5px; 
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: bold;
            }
            .print-table { 
              width: 100%; 
              border-collapse: collapse;
            }
            .print-table tr { 
              border-bottom: 1px solid #f0f0f0;
            }
            .print-table td { 
              padding: 6px 8px; 
              font-size: 11px; 
              vertical-align: top;
            }
            .print-table td:first-child { 
              width: 35%; 
              font-weight: 600; 
              color: #444;
              background: #fafafa;
              padding-left: 12px;
            }
            .print-table td:last-child { 
              width: 65%; 
              color: #1a1a1a;
            }
            .print-footer { 
              text-align: center; 
              margin-top: 25px; 
              padding-top: 12px; 
              border-top: 1px solid #e0e0e0; 
              color: #888; 
              font-size: 9px;
            }
            .print-footer p { margin-top: 2px; }
            .print-watermark {
              position: fixed;
              bottom: 20px;
              right: 20px;
              opacity: 0.1;
              font-size: 40px;
              color: #1a3a5c;
              font-weight: bold;
              transform: rotate(-20deg);
              pointer-events: none;
            }
            @media print {
              .no-print { display: none !important; }
              html, body {
                width: 210mm;
                min-height: 297mm;
              }
              body { padding: 15mm 20mm; }
            }
          </style>
        </head>
        <body>
          <div class="print-watermark">${safeSchoolName}</div>

          <div class="print-header">
            <div class="print-header-left">
              <h1>${safeSchoolName}</h1>
              <p>Student Information Report</p>
            </div>
            <div class="print-header-right">
              <p>Generated: ${new Date().toLocaleDateString()}</p>
              <p>Time: ${new Date().toLocaleTimeString()}</p>
            </div>
          </div>

          <div class="print-profile">
            <div class="print-profile-info">
              <h2>${fullName}</h2>
              <div class="sub-info">
                <span>Admission: ${student.admissionNumber}</span>
                <span>|</span>
                <span>Class: ${student.class?.name || "Not Assigned"}</span>
                <span>|</span>
                <span>Gender: ${student.gender === "male" ? "Male" : student.gender === "female" ? "Female" : "Other"}</span>
              </div>
              <div style="margin-top: 6px;">
                <span class="status-badge status-${student.status || "active"}">${student.status?.toUpperCase() || "ACTIVE"}</span>
                <span style="margin-left: 10px; font-size: 11px; color: #555;">Enrolled: ${student.enrollmentDate ? new Date(student.enrollmentDate).toLocaleDateString() : "Not set"}</span>
              </div>
            </div>
            <div class="print-profile-image">
              ${
                student.profilePicture && !imageError
                  ? `<img src="${imageUrl}" alt="${fullName}" />`
                  : `<div class="initials">${(student.firstName?.[0] || "") + (student.lastName?.[0] || "")}</div>`
              }
            </div>
          </div>

          <div class="print-section">
            <h3>Personal Information</h3>
            <table class="print-table">
              <tr><td>Full Name</td><td>${fullName}</td></tr>
              <tr><td>Admission Number</td><td>${student.admissionNumber}</td></tr>
              <tr><td>Date of Birth</td><td>${student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : "Not set"}</td></tr>
              <tr><td>Age</td><td>${student.dateOfBirth ? Math.floor((new Date() - new Date(student.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000)) + " years" : "N/A"}</td></tr>
              <tr><td>Gender</td><td>${student.gender === "male" ? "Male" : student.gender === "female" ? "Female" : "Other"}</td></tr>
              <tr><td>Nationality</td><td>${student.nationality || "Not set"}</td></tr>
              <tr><td>Religion / Tribe</td><td>${student.religion || "—"} ${student.tribe ? `/ ${student.tribe}` : ""}</td></tr>
              <tr><td>Address</td><td>${[student.address, student.city, student.state, student.country].filter(Boolean).join(", ") || "Not set"}</td></tr>
              <tr><td>Phone</td><td>${student.phone || "Not set"}</td></tr>
              <tr><td>Email</td><td>${student.email || "Not set"}</td></tr>
            </table>
          </div>

          <div class="print-section">
            <h3>Academic Information</h3>
            <table class="print-table">
              <tr><td>Current Class</td><td>${student.class?.name || "Not Assigned"}</td></tr>
              <tr><td>Enrollment Date</td><td>${student.enrollmentDate ? new Date(student.enrollmentDate).toLocaleDateString() : "Not set"}</td></tr>
              ${student.graduationDate ? `<tr><td>Graduation Date</td><td>${new Date(student.graduationDate).toLocaleDateString()}</td></tr>` : ""}
            </table>
          </div>

          ${
            student.medicalConditions || student.allergies
              ? `
          <div class="print-section">
            <h3>Medical Information</h3>
            <table class="print-table">
              <tr><td>Medical Conditions</td><td>${student.medicalConditions || "None reported"}</td></tr>
              <tr><td>Allergies</td><td>${student.allergies || "None reported"}</td></tr>
            </table>
          </div>
          `
              : ""
          }

          <div class="print-section">
            <h3>Parent / Guardian Information</h3>
            <table class="print-table">
              <tr><td>Full Name</td><td>${[student.parentFirstName, student.parentLastName].filter(Boolean).join(" ") || "Not set"}</td></tr>
              <tr><td>Relationship</td><td>${student.parentRelationship || "Not set"}</td></tr>
              <tr><td>Phone</td><td>${student.parentPhone || "Not set"}</td></tr>
              <tr><td>Email</td><td>${student.parentEmail || "Not set"}</td></tr>
              <tr><td>Address</td><td>${student.parentAddress || "Not set"}</td></tr>
              <tr><td>Occupation</td><td>${student.parentOccupation || "Not set"}</td></tr>
            </table>
          </div>

          <div class="print-footer">
            <p>This is a computer-generated document. No signature required.</p>
            <p>${safeSchoolName} | Student Information Report</p>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrint = async () => {
    if (isStarterPlan) {
      return showWarning({
        title: "PDF Download Not Available",
        text: "PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.",
      });
    }

    setIsPrinting(true);
    try {
      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(generatePrintHTML());
        printWindow.document.close();
        printWindow.document.title = `${schoolName || "School"} - Student Details`;
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to generate print preview");
    } finally {
      setIsPrinting(false);
    }
  };

  const getStatusColor = (status) => {
    return status === "active"
      ? "bg-green-100 text-green-700"
      : "bg-yellow-100 text-yellow-700";
  };

  const getGenderLabel = (gender) => {
    return gender === "male"
      ? "Male"
      : gender === "female"
        ? "Female"
        : "Other";
  };

  const getStatusIcon = (status) => {
    return status === "active" ? CheckCircleIcon : XCircleIcon;
  };

  const getInitials = () => {
    if (!student) return "";
    const first = student.firstName?.[0] || "";
    const last = student.lastName?.[0] || "";
    return `${first}${last}`.toUpperCase();
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "N/A";
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return `${age} years`;
  };

  if (loading) {
    return <FireLoader fullScreen={true} size="lg" text="LOADING..." />;
  }

  if (!student) {
    return (
      <div className="text-center py-12">
        <UserIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">
          Student Not Found
        </h2>
        <p className="text-gray-400 mb-4">
          The student you're looking for doesn't exist.
        </p>
        <button
          onClick={() => navigate("/app/students")}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Students
        </button>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(student.status);
  const showImage = imageUrl && !imageError;
  const fullName = getFullName(
    student.firstName,
    student.middleName,
    student.lastName,
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8">
      {/* Header with back button and action buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <button
          onClick={() => navigate("/app/students")}
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Students
        </button>
        <div className="flex gap-3 relative">
          <button
            type="button"
            onClick={handlePrint}
            disabled={isPrinting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPrinting ? (
              <span className="w-5 h-5 rounded-full border-2 border-gray-500 border-t-transparent animate-spin" />
            ) : (
              <PrinterIcon className="w-5 h-5" />
            )}
            {isPrinting ? "Preparing..." : "Print"}
          </button>

          {canManageStudent && (
            <button
              onClick={() => navigate(`/app/students/edit/${student.id}`)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
            >
              <PencilSquareIcon className="w-5 h-5" />
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-shrink-0 relative">
            <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center mx-auto md:mx-0 overflow-hidden">
              {showImage ? (
                <img
                  src={imageUrl}
                  alt={fullName}
                  className="w-full h-full rounded-full object-cover"
                  onError={() => setImageError(true)}
                />
              ) : (
                <span className="text-gray-700 font-bold text-4xl">
                  {getInitials()}
                </span>
              )}
            </div>

            {canManageStudent && <label
              htmlFor="profile-upload"
              className={`absolute -bottom-1 -right-1 bg-primary-600 rounded-full p-1.5 shadow-lg cursor-pointer hover:bg-primary-700 transition-colors ${
                uploading ? "opacity-70 pointer-events-none" : ""
              }`}
              title={
                isMobile
                  ? "Take photo or select from gallery"
                  : "Upload profile picture"
              }
            >
              <CameraIcon className="w-3.5 h-3.5 text-white" />
              <input
                id="profile-upload"
                type="file"
                accept="image/*"
                capture={isMobile ? "environment" : undefined}
                onChange={handleProfilePictureUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>}

            {canManageStudent && student.profilePicture && (
              <button
                onClick={handleDeleteProfilePicture}
                className="absolute -bottom-1 -left-1 bg-red-500 rounded-full p-1.5 shadow-lg cursor-pointer hover:bg-red-600 transition-colors"
                title="Remove profile picture"
              >
                <TrashIcon className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </div>

          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{fullName}</h1>
                <p className="text-gray-500">#{student.admissionNumber}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(student.status)}`}
                  >
                    <StatusIcon className="w-3 h-3" />
                    {student.status?.toUpperCase() || "UNKNOWN"}
                  </span>
                  {student.class && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {student.class.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Enrolled</p>
                <p className="text-sm font-medium text-gray-800">
                  {formatDate(student.enrollmentDate)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Personal Information
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <UserIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium text-gray-800">{fullName}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <IdentificationIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Admission Number</p>
                <p className="font-medium text-gray-800">
                  {student.admissionNumber}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="font-medium text-gray-800">
                  {formatDate(student.dateOfBirth)}
                </p>
                <p className="text-sm text-gray-500">
                  {calculateAge(student.dateOfBirth)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <UserIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="font-medium text-gray-800">
                  {getGenderLabel(student.gender)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <GlobeAltIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Nationality</p>
                <p className="font-medium text-gray-800">
                  {student.nationality || "Not set"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <UserIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Religion / Tribe</p>
                <p className="font-medium text-gray-800">
                  {student.religion || "—"}{" "}
                  {student.tribe && `/ ${student.tribe}`}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPinIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium text-gray-800">
                  {[
                    student.address,
                    student.city,
                    student.state,
                    student.country,
                  ]
                    .filter(Boolean)
                    .join(", ") || "Not set"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <PhoneIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium text-gray-800">
                  {student.phone || "Not set"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-800">
                  {student.email || "Not set"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Academic Information
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <AcademicCapIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Current Class</p>
                <p className="font-medium text-gray-800">
                  {student.class?.name || "Not Assigned"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CalendarIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Enrollment Date</p>
                <p className="font-medium text-gray-800">
                  {formatDate(student.enrollmentDate)}
                </p>
              </div>
            </div>
            {student.graduationDate && (
              <div className="flex items-start gap-3">
                <CalendarIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
                <div>
                  <p className="text-sm text-gray-500">Graduation Date</p>
                  <p className="font-medium text-gray-800">
                    {formatDate(student.graduationDate)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Medical Information (if exists) */}
          {(student.medicalConditions || student.allergies) && (
            <>
              <div className="border-t border-gray-100 my-4"></div>
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Medical Information
              </h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <HeartIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
                  <div>
                    <p className="text-sm text-gray-500">Medical Conditions</p>
                    <p className="font-medium text-gray-800">
                      {student.medicalConditions || "None reported"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <HeartIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
                  <div>
                    <p className="text-sm text-gray-500">Allergies</p>
                    <p className="font-medium text-gray-800">
                      {student.allergies || "None reported"}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Parent / Guardian Information */}
          <div className="border-t border-gray-100 my-4"></div>
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Parent / Guardian Information
          </h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <UserIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Full Name</p>
                <p className="font-medium text-gray-800">
                  {[student.parentFirstName, student.parentLastName]
                    .filter(Boolean)
                    .join(" ") || "Not set"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <HeartIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Relationship</p>
                <p className="font-medium text-gray-800 capitalize">
                  {student.parentRelationship || "Not set"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <PhoneIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                {student.parentPhone ? (
                  <a
                    href={`tel:${student.parentPhone}`}
                    className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
                  >
                    {student.parentPhone}
                  </a>
                ) : (
                  <p className="font-medium text-gray-800">Not set</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <EnvelopeIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div className="min-w-0">
                <p className="text-sm text-gray-500">Email</p>
                {student.parentEmail ? (
                  <a
                    href={`mailto:${student.parentEmail}`}
                    className="font-medium text-primary-600 hover:text-primary-700 hover:underline break-all"
                  >
                    {student.parentEmail}
                  </a>
                ) : (
                  <p className="font-medium text-gray-800">Not set</p>
                )}
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPinIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="font-medium text-gray-800 whitespace-pre-line">
                  {student.parentAddress || "Not set"}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <BriefcaseIcon className="text-gray-400 flex-shrink-0 w-5 h-5" />
              <div>
                <p className="text-sm text-gray-500">Occupation</p>
                <p className="font-medium text-gray-800">
                  {student.parentOccupation || "Not set"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
        <button
          onClick={() => navigate(`/app/students/${student.id}/grades`)}
          className="flex-1 min-w-[140px] inline-flex items-center justify-center px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors shadow-sm text-sm"
        >
          <AcademicCapIcon className="w-4 h-4 mr-2" />
          View Grade Sheet
        </button>
        <button
          onClick={() => navigate(`/app/students/${student.id}/attendance`)}
          className="flex-1 min-w-[140px] inline-flex items-center justify-center px-4 py-2.5 bg-secondary-600 text-white rounded-xl hover:bg-secondary-700 transition-colors shadow-sm text-sm"
        >
          <CalendarDaysIcon className="w-4 h-4 mr-2" />
          View Attendance
        </button>
        {canEditGrades && (
          <button
            onClick={() => navigate(`/app/students/${student.id}/grades/edit`)}
            className="flex-1 min-w-[140px] inline-flex items-center justify-center px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors shadow-sm text-sm"
          >
            <PencilSquareIcon className="w-4 h-4 mr-2" />
            Add/Edit Grades
          </button>
        )}
      </div>
    </div>
  );
}